// src/pages/AcessosLog.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Select, MenuItem, Button,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Switch,
  FormControlLabel, Grid, Stack, TableContainer, Card, CardContent, Skeleton,
  TablePagination, Checkbox
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BarChartIcon from "@mui/icons-material/BarChart";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Today from "@mui/icons-material/Today";
import RestartAlt from "@mui/icons-material/RestartAlt";
import FileDownload from "@mui/icons-material/FileDownload";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { alpha } from "@mui/material/styles";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip as ChartTooltip, CartesianGrid, Legend, BarChart, Bar, Cell
} from "recharts";

import api from "../services/api";
import SearchUserAutocomplete from "../components/SearchUserAutocomplete";

/* ================= Helpers ================= */
const TZ = "America/Sao_Paulo";
const PAGE_SIZES = [50, 100, 200, 500, 1000];

function fmtDateTimeBR(v) {
  if (!v) return "—";
  const d = new Date(String(v).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
  }).format(d);
}
function ymd(v) {
  const d = new Date(String(v).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function copy(text) {
  const t = String(text ?? "");
  if (navigator.clipboard?.writeText) { navigator.clipboard.writeText(t).catch(() => {}); return; }
  const ta = document.createElement("textarea");
  ta.value = t; document.body.appendChild(ta); ta.select();
  try { document.execCommand("copy"); } catch {}
  document.body.removeChild(ta);
}

const STATUS_STYLES = {
  ok:    { bg: "#43a047", label: "ok" },
  falha: { bg: "#e53935", label: "falha" },
  erro:  { bg: "#fb8c00", label: "erro" }
};


function StatusChip({ value }) {
  const k = (value || "").toLowerCase();
  const cfg = STATUS_STYLES[k] || { bg: "#9e9e9e", label: value || "-" };
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{ bgcolor: cfg.bg, color: "#fff", fontWeight: 700, height: 22 }}
    />
  );
}

// pinta a linha conforme o status
const rowSxByStatus = (status) => {
  const key = (status || "").toLowerCase();
  const color = STATUS_STYLES[key]?.bg;
  if (!color) return {};
  return {
    background: `linear-gradient(90deg, ${alpha(color,0.08)} 0, ${alpha(color,0.04)} 60%, transparent 100%)`,
    "&:hover": { backgroundColor: alpha(color, 0.12) },
    "& td, & th": { borderBottomColor: alpha(color, 0.25) },
    "& td:first-of-type": { borderLeft: `4px solid ${color}` }
  };
};

/* ================= Component ================= */
export default function AcessosLog() {
  // 1) ESTADOS E VARIÁVEIS
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [latencyMs, setLatencyMs] = useState(null);
  const [requestId, setRequestId] = useState(null);

  const isAdmin = (localStorage.getItem("tipo") === "admin");
  const [usuarioSel, setUsuarioSel] = useState(null);

  const [filtros, setFiltros] = useState({
    who: "", whoExact: false, acao: "", rota: "", status: "", tipo: "", de: "", ate: "",
  });

  // 2) HELPERS DERIVADOS
  const paramsLimpos = useCallback(
    () => Object.fromEntries(
      Object.entries(filtros)
        .filter(([k, v]) => v !== "" && v != null && !(k === "whoExact" && v === false))
        .map(([k, v]) => [k === "whoExact" ? "who_exact" : k, v])
    ),
    [filtros]
  );

  // 3) FUNÇÃO load
  const load = useCallback(async () => {
    setLoading(true);
    setErrorText("");
    const t0 = performance.now();
    try {
      const resp = await api.get("/acessos", {
        params: { page: page + 1, pageSize, ...paramsLimpos() },
      });
      const { data } = resp;
      setRows(Array.isArray(data?.registros) ? data.registros : []);
      setTotal(Number(data?.total || 0));
      setLatencyMs(Math.round(performance.now() - t0));
      setRequestId(resp?.headers?.["x-request-id"] || null);
    } catch (e) {
      console.error("Erro carregando acessos:", e);
      setRows([]); setTotal(0);
      setErrorText(e?.response?.data?.error || "Erro ao listar acessos.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, paramsLimpos]);

  // 4) Limpar >90 dias (admin) — usa setLoading e load já definidos
  const limparAntigos = useCallback(async () => {
    const ok = window.confirm("Apagar definitivamente os logs com mais de 90 dias?");
    if (!ok) return;
    try {
      setLoading(true);
      const { data } = await api.delete("/acessos/limpar", { params: { days: 90 } });
      alert(`Removidos ${data?.removidos ?? 0} registros com mais de ${data?.dias ?? 90} dias.`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao apagar logs antigos.");
    } finally {
      setLoading(false);
    }
  }, [load]);

  // pega revendedor.chave OU detalhes.chave (C#)
  const getUsuarioOuChave = (r) => {
    if (r?.usuario_ou_chave) return r.usuario_ou_chave;
    if (r?.usuario_nome || r?.usuario_chave) return r.usuario_nome || r.usuario_chave;
    try {
      const det = typeof r?.detalhes === "string" ? JSON.parse(r.detalhes) : r?.detalhes;
      return det?.chave || "—";
    } catch {
      return "—";
    }
  };

  // nome exibível (usuario.nome / usuario por chave / nome_cliente da licença)
  const getNomeExibir = (r) => r?.usuario_nome_exibir ?? r?.usuario_nome ?? "";

  useEffect(() => { load(); }, [load]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // ===== DETALHES =====
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgTitle, setDlgTitle] = useState("");
  const [dlgBody, setDlgBody] = useState("");
  const openDetalhes = (row) => {
    setDlgTitle(`Detalhes #${row.id}`);
    let body = row.detalhes || "";
    try {
      const parsed = typeof body === "string" ? JSON.parse(body) : body;
      body = JSON.stringify(parsed, null, 2);
    } catch {}
    setDlgBody(String(body || "—"));
    setDlgOpen(true);
  };

  // ===== AÇÕES / BUSCA =====
  const buscar = () => { setPage(0); load(); };
  const limpar = async () => {
    const hasFilters = Object.values(filtros).some(v => v) || usuarioSel != null;
    if (hasFilters) {
      const ok = window.confirm("Limpar filtros atuais?");
      if (!ok) return;
    }
    setUsuarioSel(null);
    setFiltros({ who: "", whoExact: false, acao: "", rota: "", status: "", tipo: "", de: "", ate: "" });
    setPage(0);
    setTimeout(load, 0);
  };
  const onEnter = (ev) => { if (ev.key === "Enter") buscar(); };

  // ===== EXPORTAÇÕES =====
  const exportCSV = () => {
    const headers = [
      "id","criado_em","usuario_ou_chave","usuario_nome","usuario_tipo",
      "acao","status","rota","metodo","ip","user_agent","detalhes"
    ];
    const lines = rows.map(r => {
      const usuarioOC  = getUsuarioOuChave(r);
      const nomeExibir = getNomeExibir(r);
      const detalhesStr = (typeof r.detalhes === "string" ? r.detalhes : JSON.stringify(r.detalhes || ""))
        .replace(/\n/g, " ");
      return [
        r.id, r.criado_em, usuarioOC, nomeExibir, (r.usuario_tipo || ""),
        r.acao, r.status, r.rota, r.metodo, r.ip,
        (r.user_agent || "").replace(/\n/g, " "),
        detalhesStr
      ].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",");
    });
    const csv = "\uFEFF" + [headers.join(","), ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `acessos_p${page+1}_n${pageSize}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportXLSX = async () => {
    try {
      const mod = await import("xlsx");
      const XLSX = mod.default || mod;
      const data = rows.map(r => ({
        ID: r.id,
        CriadoEm: r.criado_em,
        UsuarioOuChave: getUsuarioOuChave(r),
        UsuarioNome: getNomeExibir(r),
        UsuarioTipo: r.usuario_tipo || "",
        Acao: r.acao || "",
        Status: r.status || "",
        Rota: r.rota || "",
        Metodo: r.metodo || "",
        IP: r.ip || "",
        UserAgent: r.user_agent || "",
        DetalhesJSON: typeof r.detalhes === "string" ? r.detalhes : JSON.stringify(r.detalhes || "")
      }));
      const ws = XLSX.utils.json_to_sheet(data, { header: Object.keys(data[0] || {A:1}) });
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.min(60, Math.max(k.length, ...data.map(row => String(row[k] ?? "").length)))
      }));
      ws["!cols"] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Acessos (página)");
      XLSX.writeFile(wb, `acessos_p${page+1}_n${pageSize}.xlsx`);
    } catch (e) {
      console.error("XLSX erro:", e);
      alert("Falha ao gerar XLSX (instale a dependência 'xlsx').");
    }
  };

  // ===== CHIPS / RESUMOS / GRÁFICOS =====
  const activeChips = useMemo(() => {
    const chips = [];
    for (const [k, v] of Object.entries(filtros)) {
      if (v === "" || v == null) continue;
      if (k === "whoExact" && v === false) continue;
      let label;
      if (k === "de" || k === "ate")      label = `${k}: ${v}`;
      else if (k === "who")               label = `usuário: ${v}`;
      else if (k === "whoExact")          label = `usuário: (igual)`;
      else if (k === "acao")              label = `ação: ${v}`;
      else if (k === "rota")              label = `rota: ${v}`;
      else if (k === "status")            label = `status: ${v}`;
      else if (k === "tipo")              label = `tipo: ${v}`;
      else                                label = `${k}: ${v}`;
      chips.push({ key: k, label });
    }
    return chips;
  }, [filtros]);

  const clearChip = useCallback((key) => {
    setPage(0);
    setFiltros((f) => {
      const next = { ...f };
      if (key === "who") {
        next.who = "";
        next.whoExact = false;
        setUsuarioSel(null);
      } else if (key === "whoExact") {
        next.whoExact = false;
      } else {
        next[key] = "";
      }
      return next;
    });
    setTimeout(load, 0);
  }, [load]);

  const resumo = useMemo(() => {
    const byStatus = { ok: 0, falha: 0, erro: 0, outros: 0 };
    const users = new Set();
    rows.forEach(r => {
      const s = (r.status || "").toLowerCase();
      if (byStatus[s] != null) byStatus[s]++; else byStatus.outros++;
      users.add(getUsuarioOuChave(r));
    });
    return { byStatus, uniqueUsers: users.has("—") ? users.size - 1 : users.size, total: rows.length };
  }, [rows]);

  const grafPorDia = useMemo(() => {
    const map = {};
    rows.forEach(r => {
      const d = ymd(r.criado_em);
      map[d] ??= { dia: d, ok: 0, falha: 0, erro: 0, total: 0 };
      map[d].total++;
      const s = (r.status || "").toLowerCase();
      if (s === "ok") map[d].ok++;
      else if (s === "falha") map[d].falha++;
      else if (s === "erro") map[d].erro++;
    });
    return Object.values(map).sort((a,b)=>a.dia.localeCompare(b.dia));
  }, [rows]);

  const grafPorStatus = useMemo(() => ([
    { status: "ok",    valor: resumo.byStatus.ok },
    { status: "falha", valor: resumo.byStatus.falha },
    { status: "erro",  valor: resumo.byStatus.erro },
  ]), [resumo]);

  // filtros rápidos
  const setHoje = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const dd = String(d.getDate()).padStart(2,"0");
    const day = `${yyyy}-${mm}-${dd}`;
    setFiltros(f => ({ ...f, de: day, ate: day }));
  };
  const setTrintaDias = () => {
    const hoje = new Date();
    const ini  = new Date(); ini.setDate(hoje.getDate()-29);
    const y = (d)=>d.getFullYear();
    const m = (d)=>String(d.getMonth()+1).padStart(2,"0");
    const d2 = (d)=>String(d.getDate()).padStart(2,"0");
    setFiltros(f => ({
      ...f,
      de:  `${y(ini)}-${m(ini)}-${d2(ini)}`,
      ate: `${y(hoje)}-${m(hoje)}-${d2(hoje)}`
    }));
  };

  // ===== RENDER =====
  return (
    <Box p={2} sx={{ display: "grid", gap: 2, maxWidth: 1300, mx: "auto" }}>
      {/* Cabeçalho */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={800}>Logs de Acesso</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {total} registro(s)
              {activeChips.length ? " · filtros ativos" : ""}
              {latencyMs != null ? ` · carregado em ${latencyMs} ms` : ""}
              {requestId ? ` · id ${requestId}` : ""}
            </Typography>
            {requestId && (
              <Tooltip title="Copiar request_id">
                <IconButton size="small" onClick={() => copy(requestId)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            sx={{ mr: 1 }}
            control={<Switch checked={!!autoRefresh} onChange={(e)=>setAutoRefresh(e.target.checked)} size="small" />}
            label="Auto"
          />
          {isAdmin && (
            <Tooltip title="Apagar logs com mais de 90 dias (irreversível)">
              <span>
                <Button
                  onClick={limparAntigos}
                  size="small"
                  color="error"
                  startIcon={<DeleteSweepIcon/>}
                  variant="outlined"
                  disabled={loading}
                >
                  Apagar &gt; 90d
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Recarregar">
            <span>
              <Button onClick={load} size="small" startIcon={<RefreshIcon/>} variant="outlined">Recarregar</Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Aviso de erro */}
      {errorText ? (
        <Paper sx={{ p: 1.5, borderLeft: "4px solid #e53935", bgcolor: "#ffebee" }}>
          <Typography variant="body2" color="error">{errorText}</Typography>
        </Paper>
      ) : null}

      {/* Filtros */}
      <Paper sx={{ p: 1, width: "100%" }} onKeyDown={onEnter}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {isAdmin && (
            <>
              <SearchUserAutocomplete
                value={usuarioSel}
                onChange={(val) => {
                  setUsuarioSel(val);
                  setFiltros(f => ({ ...f, who: val?.chave || "" }));
                  setPage(0);
                }}
                sx={{ flex: "1 1 280px", minWidth: 280 }}
              />
              <Button
                size="small"
                onClick={() => {
                  setUsuarioSel(null);
                  setFiltros(f => ({ ...f, who: "" }));
                  setPage(0);
                  load();
                }}
                sx={{ ml: 0.5, whiteSpace: "nowrap" }}
              >
                Ver todos
              </Button>
            </>
          )}

          <Select
            size="small"
            displayEmpty
            value={filtros.status}
            onChange={(e)=>setFiltros(f=>({...f, status: e.target.value}))}
            sx={{ width: 130 }}
          >
            <MenuItem value=""><em>Status</em></MenuItem>
            <MenuItem value="ok">ok</MenuItem>
            <MenuItem value="falha">falha</MenuItem>
            <MenuItem value="erro">erro</MenuItem>
          </Select>

          <TextField
            label="De"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.de}
            onChange={(e)=>setFiltros(f=>({...f, de: e.target.value}))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />
          <TextField
            label="Até"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtros.ate}
            onChange={(e)=>setFiltros(f=>({...f, ate: e.target.value}))}
            sx={{ width: 130 }}
            InputProps={{ sx: { height: 34, fontSize: 14 } }}
          />

          <Button startIcon={<Today />} variant="outlined" size="small" onClick={() => { setHoje(); setTimeout(buscar, 0); }} sx={{ px: 1, whiteSpace: "nowrap" }}>
            HOJE
          </Button>
          <Button startIcon={<Today />} variant="outlined" size="small" onClick={() => { setTrintaDias(); setTimeout(buscar, 0); }} sx={{ px: 1, whiteSpace: "nowrap" }}>
            30 DIAS
          </Button>

          <Button variant="contained" size="small" onClick={buscar} disabled={loading} sx={{ px: 2, whiteSpace: "nowrap" }}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null} FILTRAR
          </Button>
          <Button startIcon={<RestartAlt />} variant="outlined" size="small" onClick={limpar} sx={{ px: 1, minWidth: 0, whiteSpace: "nowrap" }}>
            LIMPAR
          </Button>

          {/* quebra */}
          <Box sx={{ flexBasis: "100%" }} />

          {/* LINHA 2 */}
          <TextField
            label="Usuário (chave ou nome)"
            size="small"
            value={filtros.who}
            onChange={(e)=>setFiltros(f=>({...f, who: e.target.value}))}
            sx={{ width: 220 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!filtros.whoExact}
                onChange={(e)=>setFiltros(f=>({...f, whoExact: e.target.checked}))}
              />
            }
            label="igual"
          />

          <TextField
            label="Ação"
            size="small"
            value={filtros.acao}
            onChange={(e)=>setFiltros(f=>({...f, acao: e.target.value}))}
            sx={{ width: 180 }}
            placeholder="login / licenca_atualizar"
          />
          <TextField
            label="Rota"
            size="small"
            value={filtros.rota}
            onChange={(e)=>setFiltros(f=>({...f, rota: e.target.value}))}
            sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: 420 }}
            placeholder="/api/kit-blindagem"
          />
          <Select
            size="small"
            displayEmpty
            value={filtros.tipo}
            onChange={(e)=>setFiltros(f=>({...f, tipo: e.target.value}))}
            sx={{ width: 160 }}
          >
            <MenuItem value=""><em>Tipo</em></MenuItem>
            <MenuItem value="admin">admin</MenuItem>
            <MenuItem value="revendedor">revendedor</MenuItem>
          </Select>

          <Button startIcon={<FileDownload />} variant="outlined" size="small" onClick={exportCSV} sx={{ px: 1, minWidth: 0, whiteSpace: "nowrap" }}>
            CSV (página)
          </Button>
          <Button startIcon={<FileDownload />} variant="outlined" size="small" onClick={exportXLSX} sx={{ px: 1, minWidth: 0, whiteSpace: "nowrap" }}>
            XLSX (página)
          </Button>
        </Box>
      </Paper>

      {activeChips.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {activeChips.map(c => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              onDelete={() => clearChip(c.key)}
              deleteIcon={<CloseIcon fontSize="small" />}
            />
          ))}
        </Stack>
      )}

      {/* Cards de resumo */}
      <Grid container columnSpacing={2} rowSpacing={2}>
        {["ok","falha","erro"].map(k => (
          <Grid item key={k} xs={12} sm={6} md={3}>
            <Card
              onClick={() => {
                setFiltros(f => ({ ...f, status: f.status === k ? "" : k }));
                setPage(0);
                setTimeout(load, 0);
              }}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                boxShadow: 1,
                background: `linear-gradient(180deg, ${STATUS_STYLES[k].bg}22, #fff)`,
                borderTop: `5px solid ${STATUS_STYLES[k].bg}`,
                transition: "transform .06s ease",
                outline: filtros.status === k ? `2px solid ${STATUS_STYLES[k].bg}` : "none",
                "&:hover": { transform: "translateY(-2px)" }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: STATUS_STYLES[k].bg, fontWeight: 800 }}>
                  {STATUS_STYLES[k].label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                  {resumo.byStatus[k]}
                </Typography>
                <Typography variant="caption" color="text.secondary">registros (página)</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2, boxShadow: 1,
            background: `linear-gradient(180deg, #3f51b522, #fff)`,
            borderTop: `5px solid #3f51b5`
          }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="overline" sx={{ color: "#3f51b5", fontWeight: 800 }}>
                usuários únicos
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                {resumo.uniqueUsers}
              </Typography>
              <Typography variant="caption" color="text.secondary">na página</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container columnSpacing={2} rowSpacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <BarChartIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>Registros por dia (página)</Typography>
            </Stack>
            {loading ? (
              <Skeleton variant="rounded" height={220} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={grafPorDia}>
                  <XAxis dataKey="dia" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <ChartTooltip />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Legend />
                  <Line type="monotone" dataKey="ok"    stroke={STATUS_STYLES.ok.bg}    strokeWidth={2} dot />
                  <Line type="monotone" dataKey="falha" stroke={STATUS_STYLES.falha.bg} strokeWidth={2} dot />
                  <Line type="monotone" dataKey="erro"  stroke={STATUS_STYLES.erro.bg}  strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <FilterAltIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>Por status (página)</Typography>
            </Stack>
            {loading ? (
              <Skeleton variant="rounded" height={220} />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={grafPorStatus}>
                  <XAxis dataKey="status" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <ChartTooltip />
                  <Bar dataKey="valor" radius={[6, 6, 0, 0]}>
                    {grafPorStatus.map(d => (
                      <Cell key={d.status} fill={STATUS_STYLES[d.status]?.bg || "#90caf9"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela */}
      <Paper sx={{ overflowX: "auto" }}>
        <Box sx={{ position: "relative" }}>
          {loading && (
            <Box sx={{
              position: "absolute", inset: 0, display: "flex",
              alignItems: "center", justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.6)", zIndex: 1
            }}>
              <CircularProgress />
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Usuário/Chave</TableCell>
                  <TableCell>Ação</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rota</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>User-Agent</TableCell>
                  <TableCell>Detalhes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!rows || rows.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      Nenhum registro encontrado.
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((r) => (
                  <TableRow key={r.id} hover sx={rowSxByStatus(r.status)}>
                    <TableCell>{fmtDateTimeBR(r.criado_em)}</TableCell>

                    <TableCell
                      sx={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      title={`${getUsuarioOuChave(r)}${r.usuario_tipo ? ` · ${r.usuario_tipo}` : ""}${r.usuario_nome_exibir ? `\n${r.usuario_nome_exibir}` : ""}`}
                    >
                      <Stack direction="row" gap={0.5} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.5 }}
                            noWrap
                          >
                            {getUsuarioOuChave(r)}
                          </Typography>
                          {r.usuario_nome_exibir && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", lineHeight: 1.2 }}
                              noWrap
                              title={r.usuario_nome_exibir}
                            >
                              {r.usuario_nome_exibir}
                            </Typography>
                          )}
                        </Box>

                        <Tooltip title="Copiar chave">
                          <IconButton size="small" onClick={() => copy(getUsuarioOuChave(r))}>
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    <TableCell>{r.acao}</TableCell>
                    <TableCell><StatusChip value={r.status} /></TableCell>

                    <TableCell title={r.rota} sx={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <Stack direction="row" gap={0.5} alignItems="center">
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.rota}</span>
                        <Tooltip title="Copiar rota">
                          <IconButton size="small" onClick={()=>copy(r.rota)}><ContentCopyIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    <TableCell>{r.metodo}</TableCell>

                    <TableCell>
                      <Stack direction="row" gap={0.5} alignItems="center">
                        <span>{r.ip}</span>
                        <Tooltip title="Copiar IP">
                          <IconButton size="small" onClick={()=>copy(r.ip)}><ContentCopyIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    <TableCell title={r.user_agent} sx={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      <Stack direction="row" gap={0.5} alignItems="center">
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.user_agent}</span>
                        <Tooltip title="Copiar User-Agent">
                          <IconButton size="small" onClick={()=>copy(r.user_agent)}><ContentCopyIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Button size="small" onClick={() => openDetalhes(r)}>ver</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={PAGE_SIZES}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            labelRowsPerPage="Linhas por página:"
          />
        </Box>
      </Paper>

      {/* Detalhes */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {dlgTitle}
          <IconButton onClick={() => setDlgOpen(false)} size="small"><CloseIcon/></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="pre" sx={{
            m: 0,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere"
          }}>
            {dlgBody}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(dlgBody)}>
            Copiar JSON
          </Button>
          <Button size="small" onClick={() => setDlgOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

